'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import CourseCard from './CourseCard';
import SearchBar from './SearchBar';
import ViewControls from './ViewControls';
import ComparisonMatrix from './ComparisonMatrix';
import { supabase } from '@/lib/supabase';

interface Course {
  id: string;
  title: string;
  instructor: string;
  instructorImage: string;
  instructorImageAlt: string;
  duration: string;
  level: string;
  rating: number;
  studentsEnrolled: number;
  price: number;
  thumbnail: string;
  thumbnailAlt: string;
  category: string;
  format: string;
  startDate: string;
  description: string;
}

const CourseCatalogInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('popularity');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsHydrated(true);
    
    // Use promise chain instead of async/await
    fetchCourses();
  }, []);

  const fetchCourses = () => {
    setLoading(true);
    
    supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        price,
        duration_weeks,
        thumbnail_url,
        average_rating,
        is_active,
        user_profiles!courses_instructor_id_fkey(full_name, avatar_url)
      `)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(2)
      .then(({ data, error }) => {
        if (error) {
          console.error('Supabase error:', error);
          setCourses([]);
          return;
        }

        const formattedCourses: Course[] = (data || []).map((course: any) => ({
          id: course.id,
          title: course.title,
          instructor: course.user_profiles?.full_name || 'Instructor',
          instructorImage: course.user_profiles?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1a9e8814c-1763296696290.png',
          instructorImageAlt: `${course.user_profiles?.full_name || 'Instructor'} profile picture`,
          duration: `${course.duration_weeks || 8} weeks`,
          level: 'Beginner',
          rating: parseFloat(course.average_rating || '4.8'),
          studentsEnrolled: 0,
          price: parseFloat(course.price || '0'),
          thumbnail: course.thumbnail_url || 'https://images.unsplash.com/photo-1632910605645-7a2e40713db1',
          thumbnailAlt: `${course.title} course thumbnail`,
          category: course.title.includes('Prompt') ? 'AI & Technology' : 'Marketing',
          format: 'Live + Recorded',
          startDate: 'Feb 1, 2026',
          description: course.description || ''
        }));

        setCourses(formattedCourses);
      })
      .catch((error) => {
        console.error('Error fetching courses:', error);
        setCourses([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const searchSuggestions = [
    'Prompt Engineering',
    'AI',
    'Digital Marketing',
    'Marketing',
    'Technology'
  ];

  const handleToggleComparison = (courseId: string) => {
    setSelectedForComparison((prev) => {
      if (prev.includes(courseId)) {
        return prev.filter((id) => id !== courseId);
      }
      if (prev.length < 3) {
        return [...prev, courseId];
      }
      return prev;
    });
  };

  const handleRemoveFromComparison = (courseId: string) => {
    setSelectedForComparison((prev) => prev.filter((id) => id !== courseId));
  };

  const filteredCourses = courses.filter((course) => {
    const matchesSearch =
      searchQuery === '' ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.category.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    switch (sortBy) {
      case 'popularity':
        return b.studentsEnrolled - a.studentsEnrolled;
      case 'rating':
        return b.rating - a.rating;
      case 'newest':
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      default:
        return 0;
    }
  });

  const comparisonCourses = courses.filter((course) =>
    selectedForComparison.includes(course.id)
  );

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-12 bg-muted rounded w-1/3 mb-8" />
            <div className="h-64 bg-muted rounded mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-headline font-bold text-foreground mb-4">
            Our Courses
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            Explore our expert-led courses designed to transform your career with live instruction and hands-on projects.
          </p>
        </div>

        <div className="mb-8">
          <SearchBar onSearch={setSearchQuery} suggestions={searchSuggestions} />
        </div>

        <div className="flex-1">
          <ViewControls
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            sortBy={sortBy}
            onSortChange={setSortBy}
            totalResults={sortedCourses.length}
            onToggleMobileFilters={() => {}} />

          {selectedForComparison.length > 0 && (
            <div className="mb-6 p-4 bg-brand-secondary border border-brand-primary rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="ScaleIcon" size={20} variant="outline" className="text-brand-primary" />
                  <span className="text-sm font-medium text-brand-primary">
                    {selectedForComparison.length} course(s) selected for comparison
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowComparison(true)}
                    disabled={selectedForComparison.length < 2}
                    className="px-4 py-2 bg-brand-primary text-white text-sm font-semibold rounded-md hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                    Compare Courses
                  </button>
                  <button
                    onClick={() => setSelectedForComparison([])}
                    className="px-4 py-2 text-brand-primary text-sm font-medium hover:bg-brand-primary hover:bg-opacity-10 rounded-md transition-colors">
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}

          {sortedCourses.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="MagnifyingGlassIcon" size={64} variant="outline" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                No courses found
              </h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search query to find courses.
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="px-6 py-2 bg-primary text-white font-semibold rounded-md hover:bg-opacity-90 transition-all">
                Clear Search
              </button>
            </div>
          ) : (
            <div
              className={
                viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-6'
              }>
              {sortedCourses.map((course) => (
                <div key={course.id} className="relative">
                  <CourseCard course={course} viewMode={viewMode} />
                  <button
                    onClick={() => handleToggleComparison(course.id)}
                    className={`absolute top-4 right-4 p-2 rounded-lg shadow-lg transition-all ${
                      selectedForComparison.includes(course.id)
                        ? 'bg-brand-primary text-white' :'bg-card text-muted-foreground hover:bg-muted'
                    }`}
                    aria-label="Add to comparison">
                    <Icon
                      name="ScaleIcon"
                      size={20}
                      variant={selectedForComparison.includes(course.id) ? 'solid' : 'outline'} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showComparison && (
        <ComparisonMatrix
          courses={comparisonCourses}
          onRemoveCourse={handleRemoveFromComparison}
          onClose={() => setShowComparison(false)} />
      )}
    </div>
  );
};

export default CourseCatalogInteractive;